import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Menu, X, Newspaper, Moon, Sun } from "lucide-react";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  
  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === "/";

  // Load dark mode preference on mount
  useEffect(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved === 'true') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/cerca?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  // Logo component with color switching
  const Logo = ({ inverted = false }) => (
    <img 
      src="/icons/Logo_Sagre.svg" 
      alt="Sagre Oggi" 
      className={`h-8 w-auto ${inverted ? 'brightness-0 invert' : 'dark:brightness-0 dark:invert'}`}
    />
  );

  return (
    <nav className={`sticky top-0 z-50 transition-all duration-200 ${isHome ? 'bg-black/20 backdrop-blur-sm' : 'bg-white dark:bg-gray-900 border-b border-border dark:border-gray-800'}`} data-testid="navbar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="logo-link"
          >
            <Logo inverted={isHome} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {!isHome && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSearchOpen(!searchOpen)}
                className="rounded-lg h-9 px-3 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <Search className="w-4 h-4" />
              </Button>
            )}
            
            {/* Dark Mode Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleDarkMode}
              className={`rounded-lg h-9 px-3 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
              aria-label="Toggle dark mode"
              title={darkMode ? "Modalità chiara" : "Modalità scura"}
            >
              {darkMode ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </Button>

            <Link to="/cerca">
              <Button 
                variant={isActive("/cerca") ? "secondary" : "ghost"}
                size="sm"
                className={`rounded-lg h-9 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
                data-testid="search-nav-btn"
              >
                Esplora
              </Button>
            </Link>
            <Link to="/news">
              <Button 
                variant={isActive("/news") ? "secondary" : "ghost"}
                size="sm"
                className={`rounded-lg h-9 gap-1.5 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
                data-testid="news-nav-btn"
              >
                <Newspaper className="w-3.5 h-3.5" />
                News
              </Button>
            </Link>
            <Link to="/aggiungi">
              <Button 
                variant={isActive("/aggiungi") ? "secondary" : "ghost"}
                size="sm"
                className={`rounded-lg h-9 gap-1.5 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
                data-testid="add-sagra-nav-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Aggiungi
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className={`md:hidden rounded-lg h-9 w-9 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>

        {/* Search Bar (Desktop) */}
        {searchOpen && !isHome && (
          <div className="hidden md:block pb-3 animate-fade-in">
            <form onSubmit={handleSearch} className="max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca sagre..."
                  className="pl-9 h-9 rounded-lg bg-muted/50 border-0 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
                  autoFocus
                />
              </div>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden py-3 border-t animate-fade-in ${isHome ? 'border-white/20' : 'border-border dark:border-gray-800'}`}>
            <div className="flex flex-col gap-1">
              {/* Dark Mode Toggle Mobile */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleDarkMode}
                className={`w-full justify-start rounded-lg h-9 gap-1.5 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
              >
                {darkMode ? (
                  <>
                    <Sun className="w-3.5 h-3.5" />
                    Modalità Chiara
                  </>
                ) : (
                  <>
                    <Moon className="w-3.5 h-3.5" />
                    Modalità Scura
                  </>
                )}
              </Button>

              <Link to="/cerca" onClick={() => setMobileMenuOpen(false)}>
                <Button 
                  variant={isActive("/cerca") ? "secondary" : "ghost"}
                  size="sm"
                  className={`w-full justify-start rounded-lg h-9 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
                >
                  Esplora Sagre
                </Button>
              </Link>
              <Link to="/news" onClick={() => setMobileMenuOpen(false)}>
                <Button 
                  variant={isActive("/news") ? "secondary" : "ghost"}
                  size="sm"
                  className={`w-full justify-start rounded-lg h-9 gap-1.5 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
                >
                  <Newspaper className="w-3.5 h-3.5" />
                  News
                </Button>
              </Link>
              <Link to="/aggiungi" onClick={() => setMobileMenuOpen(false)}>
                <Button 
                  variant={isActive("/aggiungi") ? "secondary" : "ghost"}
                  size="sm"
                  className={`w-full justify-start rounded-lg h-9 gap-1.5 ${isHome ? 'text-white hover:bg-white/20' : 'dark:text-gray-300 dark:hover:bg-gray-800'}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Aggiungi Sagra
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;